/**
 * Brand token validation — the guardrails, as code rather than prose.
 *
 * A brand supplies VALUES for the contract's names (`tokens.scss`). Two of
 * those choices are correctness rather than taste, and both have been got wrong
 * on real brands, so they are checked rather than trusted.
 *
 * ## 1. Contrast is checked on the ROLE, not on the colour
 *
 * A brand's ink can be perfectly good as a FILL and unusable as TYPE. Measured
 * from the anchors:
 *
 *   Team Liquid navy  #001E96   1.5:1 on the page background
 *   GCU purple        #522398   2.0:1
 *
 * Set type in either and the page goes blank. So `--accent` paints fills and
 * may be any brand value; `--accent-ink` sets type and must clear 4.5:1. Same
 * hue, lifted until it passes — it still reads as the brand.
 *
 * ## 2. The usable ink FLIPS with the theme
 *
 * 7-Eleven is the proof, and it is why "pick the brand colour" is not a
 * strategy:
 *
 *   orange #FF6C00   7.05 on dark   2.84 on white
 *   green  #005840   2.35 on dark   8.32 on white
 *
 * Same two values, swapped roles. Choosing by pixel share gives green, which is
 * unreadable on dark; choosing by contrast alone loses the green entirely.
 *
 * ## 3. Never brown
 *
 * Warm near-blacks read as brown. Dark surfaces stay neutral; the accent
 * supplies the warmth. The first 7-Eleven pass used #0A0806 / #15100C / #1C1610
 * "to keep the warmth of the source's cream" and the whole page read brown.
 */

export interface Rgb {
    r: number; g: number; b: number
}

/** Parse #rgb or #rrggbb. Returns null rather than throwing on anything else. */
export function parseHex(hex: string): Rgb | null {
    const h = hex.trim().replace(/^#/, "");
    if (h.length === 3 && (/^[0-9a-f]{3}$/i).test(h)) {
        return {
            r: parseInt(h[0] + h[0], 16),
            g: parseInt(h[1] + h[1], 16),
            b: parseInt(h[2] + h[2], 16),
        };
    }
    if (h.length === 6 && (/^[0-9a-f]{6}$/i).test(h)) {
        return {
            r: parseInt(h.slice(0, 2), 16),
            g: parseInt(h.slice(2, 4), 16),
            b: parseInt(h.slice(4, 6), 16),
        };
    }
    return null;
}

/** WCAG 2.1 relative luminance. */
export function relativeLuminance({r, g, b}: Rgb): number {
    const channel = (v: number): number => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG 2.1 contrast ratio, 1–21. Order-independent. */
export function contrastRatio(a: string, b: string): number | null {
    const ca = parseHex(a);
    const cb = parseHex(b);
    if (!ca || !cb) return null;
    const la = relativeLuminance(ca);
    const lb = relativeLuminance(cb);
    const [hi, lo] = la > lb ? [la, lb] : [lb, la];
    return (hi + 0.05) / (lo + 0.05);
}

/**
 * A dark surface that reads brown.
 *
 * ## The recorded rule was wrong, and testing it is how that surfaced
 *
 * As written in the anchors' README the check is `R > G >= B, max < 170,
 * R - B > 12`. **It does not catch its own examples.** The three colours that
 * document names as having made the whole 7-Eleven page read brown measure:
 *
 *   #0A0806   R-B = 4      #15100C   R-B = 9      #1C1610   R-B = 12
 *
 * None satisfies `> 12`. The absolute channel gap cannot work near black: at
 * these luminances a severe warm cast is only a few points wide. What is large
 * is the RATIO — all three sit at 0.40-0.43 of their own maximum, while the
 * neutral near-blacks the other anchors use (#0F0F0F, #151515, #16161b) sit at
 * 0.00 or below.
 *
 * So the test is relative chroma, and the two bounds still do their jobs:
 *   - `max < 170` keeps it to DARK surfaces. A cream page in light mode is
 *     legitimately warm and is the source's own value (#FFF6E8 is 0.09 anyway).
 *   - a warm ACCENT is not a surface — #FF6C00 is 1.00 but max is 255.
 */
export const BROWN_CHROMA = 0.15;

export function isBrown(hex: string): boolean {
    const c = parseHex(hex);
    if (!c) return false;
    const max = Math.max(c.r, c.g, c.b);
    if (max === 0) return false; /* pure black is neutral */
    if (!(c.r > c.g && c.g >= c.b)) return false;
    if (max >= 170) return false;
    return (c.r - c.b) / max > BROWN_CHROMA;
}

export const AA_NORMAL = 4.5;

export interface BrandTokens {
    /** Page ground. Must not read brown. */
    bg: string;
    /** Raised surfaces. Must not read brown. */
    surface?: string;
    /** Fills only — washes, meters, crests. Any brand value is permitted. */
    accent: string;
    /** Type. Must clear 4.5:1 against `bg`. */
    accentInk?: string;
    /** Body type. Must clear 4.5:1 against `bg`. */
    text?: string;
}

export interface TokenFinding {
    token: string;
    rule: "brown" | "contrast" | "parse";
    message: string;
    /** Measured ratio where the rule is contrast. */
    ratio?: number;
}

/**
 * Validate a brand's values against the contract.
 *
 * Returns findings rather than throwing: an operator authoring a theme should
 * see everything wrong at once, not the first thing wrong.
 */
export function validateBrandTokens(t: BrandTokens): TokenFinding[] {
    const findings: TokenFinding[] = [];

    for (const [token, value] of Object.entries(t)) {
        if (typeof value === "string" && !parseHex(value)) {
            findings.push({token, rule: "parse", message: `${value} is not a hex colour`});
        }
    }

    for (const [token, value] of [["bg", t.bg], ["surface", t.surface]] as const) {
        if (value && isBrown(value)) {
            findings.push({
                token,
                rule: "brown",
                message: `${value} reads brown. Dark surfaces stay neutral; the accent supplies the warmth.`,
            });
        }
    }

    /*
     * Type roles only. `accent` is deliberately NOT checked — it paints fills,
     * and requiring 4.5:1 of it is what forces brands to abandon their own
     * colour for a tint of it.
     */
    for (const [token, value] of [["accentInk", t.accentInk], ["text", t.text]] as const) {
        if (!value) continue;
        const ratio = contrastRatio(value, t.bg);
        if (ratio !== null && ratio < AA_NORMAL) {
            findings.push({
                token,
                rule: "contrast",
                ratio,
                message: `${value} is ${ratio.toFixed(2)}:1 on ${t.bg} — type needs ${AA_NORMAL}:1. ` +
                    "Keep the hue and lift it until it passes; it still reads as the brand.",
            });
        }
    }

    return findings;
}
